import {
  Button, Form, Row, Spin, Tooltip,
} from 'antd';
import { useEffect, useState } from 'react';
import LastStep, {
  TipFormValues,
  valueMapDollar,
} from '../../../CreateAccount/Steps/LastStep';
import { IconInfo } from '../../../../Common/Icon';

import { SubCategoryTipCreateData } from '../../../../../hooks/api/subCategoryTip';
import { useTipCreate } from './useTipCreate';
import { useTip } from './useTip';
import { useAuth } from '../../../../../store/auth';
import useAuthModals from '../../../../Layout/Menu/useAuthModals';

import styles from './index.module.scss';

interface TipFormProps {
  subCategoryId: string;
  afterSubmit?: () => void;
}

function TipForm({ subCategoryId, afterSubmit }: TipFormProps) {
  const { user, authorized } = useAuth();
  const { content, openLoginModal } = useAuthModals();
  const { createTip, loading: createTipLoading } = useTipCreate();
  const {
    getTip, response, loading: getTipLoading, error,
  } = useTip();
  const [form] = Form.useForm<TipFormValues>();
  const [resubmitMode, setResubmitMode] = useState(false);

  const handleSubmit = async () => {
    if (!authorized) {
      openLoginModal();

      return;
    }
    if (user?.country?.id !== 233) {
      console.log('user not allowed to take survey since he not from US');

      return;
    }

    const values: TipFormValues = await form.validateFields();

    const data: SubCategoryTipCreateData = {
      tipType: values.tipType,
    };

    if (
      values.tipType === '%'
      || (values.tipValue && valueMapDollar.includes(values.tipValue))
    ) {
      data.value = values.tipValue as number;
    } else {
      data.customValue = values.tipValue as number;
    }

    createTip(data, subCategoryId).then((res) => {
      if (res?.success) afterSubmit?.();
    });
    setResubmitMode(false);
  };

  useEffect(() => {
    if (!subCategoryId) return;
    if (!user?.id) {
      console.warn('Unauthorized. Ignoring "getTip" status');

      return;
    }

    getTip(subCategoryId);
  }, [subCategoryId, user]);

  useEffect(() => {
    if (response?.data) {
      const newValues: TipFormValues = {
        tipType: response.data.tipType,
        tipValue: response.data.customValue || response.data.value,
      };

      form.setFieldsValue(newValues);
    }
  }, [response, form]);

  const handleResubmit = () => setResubmitMode(true);

  return (
    <Spin spinning={getTipLoading}>
      {/* dark theme for this page */}
      <div className={styles.wrapper} data-theme="dark">
        {/* inline override to force white marks on the slider in dark theme */}
        <style>{`
          [data-theme="dark"] .ant-slider .ant-slider-mark .ant-slider-mark-text,
          [data-theme="dark"] .ant-slider .ant-slider-mark .ant-slider-mark-text.ant-slider-mark-text-active,
          [data-theme="dark"] .ant-slider .ant-slider-mark .ant-slider-mark-text span {
            color: rgba(255, 255, 255, 0.8) !important;
          }
        `}
        </style>

        {!error && response?.data != null && !resubmitMode ? (
          <div>
            <h2 className={styles.title}>Thank you for sharing!</h2>
            <p
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                flexDirection: 'column',
                lineHeight: '1.6',
              }}
            >
              Your input is valuable to the TippingGuide.com community.
            </p>
            <Row justify="center">
              <Button
                type="primary"
                onClick={handleResubmit}
                className={styles.btnSubmit}
              >
                Resubmit
              </Button>
            </Row>
          </div>
        ) : (
          <>
            <LastStep
              form={form}
              title={(
                <h2 className={styles.title}>
                  How much do you tip for average service?
                  <Tooltip
                    placement="topRight"
                    showArrow={false}
                    openClassName={styles.tooltip}
                    color="#282C35"
                    styles={{
                      body: {
                        padding: '16px',
                        minWidth: 300,
                        borderRadius: 8,
                        border: '1px solid rgba(0, 0, 0, 0.20)',
                        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                      },
                    }}
                    title={(
                      <span className={styles.tooltipText}>
                        <span className={styles.bold}>Average service</span>{' '}
                        refers to service that meets basic expectations without
                        standing out as exceptional or poor.
                      </span>
                    )}
                  >
                    <IconInfo className={styles.tooltipIcon} />
                  </Tooltip>
                </h2>
              )}
            />
            <Row justify="center">
              <Button
                onClick={handleSubmit}
                className={styles.btnSubmit}
                type="primary"
                htmlType="submit"
                loading={createTipLoading}
              >
                Submit
              </Button>
              {content}
            </Row>
          </>
        )}
      </div>
    </Spin>
  );
}

export default TipForm;
